<template>
  <div
    class="sui-search"
    :class="{
      'sui-search--focused': isFocused,
    }"
  >
    <svg class="sui-search__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 21L16 16M16.053 6.06799C18.8103 8.82528 18.8103 13.2957 16.053 16.053C13.2957 18.8103 8.82527 18.8103 6.06797 16.053C3.31068 13.2957 3.31068 8.82528 6.06797 6.06799C8.82527 3.31069 13.2957 3.31069 16.053 6.06799Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <input
      v-model="inputValue"
      type="text"
      :placeholder="placeholder"
      class="sui-search__input"
      @focus="onFocus"
      @blur="onBlur"
    />
    <a
      v-if="inputValue"
      href="#"
      class="sui-search__close"
      @click.prevent="clear"
    >
      <svg class="sui-search__close-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6.35632L6 18.3563M18 18.3563L6 6.35632" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </a>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Поиск',
  },
})

const emit = defineEmits(['update:modelValue'])

const isFocused = ref(false)

const inputValue = computed<string>({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const clear = () => {
  inputValue.value = ''
}

const onFocus = () => {
  isFocused.value = true
}

const onBlur = () => {
  isFocused.value = false
}
</script>

<style lang="scss" scoped>
@mixin placeholder {
  &::-webkit-input-placeholder { @content; }
  &:-moz-placeholder           { @content; }
  &::-moz-placeholder          { @content; }
  &:-ms-input-placeholder      { @content; }
}

.sui-search {
  position: relative;

  &__icon {
    position: absolute;
    top: 50%;
    left: 16px;
    transform: translateY(-50%);
    color: var(--color-icon-tertiary);
    transition: color .2s;
  }

  &__input {
    width: 100%;
    height: 38px;
    padding-left: 48px;
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--color-text-primary);
    background-color: var(--color-bg-tertiary);
    border: 1px solid var(--stroke-cards);
    border-radius: 8px;
    outline: none;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    transition: color .2s;

    @include placeholder {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: var(--color-text-secondary);
    }

    &::-ms-reveal,
    &::-ms-clear {
      display: none;
    }
  }

  &__close {
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    text-decoration: none;

    &-icon {
      color: var(--color-icon-tertiary);
      transition: color .2s;
    }
  }

  &:hover:not(.sui-search--focused) {
    .sui-search {
      &__icon {
        color: var(--color-icon-secondary);
      }

      &__input {
        border-color: var(--stroke-select);
      }

      &__close-icon {
        color: var(--color-icon-secondary);
      }
    }
  }

  &--focused {
    .sui-search {
      &__icon {
        color: var(--color-icon-primary);
      }

      &__input {
        background-color: var(--color-bg-primary);
        border-color: var(--color-icon-primary);
      }

      &__close-icon {
        color: var(--color-icon-primary);
      }
    }
  }
}
</style>
